import React, { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import "./sidebar.css";
import { useAuth0 } from "@auth0/auth0-react";
import dmiLogo from "../../images/DeepMedIQ-small.jpeg";

/* ─── Inline Auth Form (used for both Login & Signup inside sidebar) ─── */
function SidebarAuthForm({ mode, onSwitchMode, onClose }) {
  const { loginWithRedirect } = useAuth0();
  const [passwordVisible, setPasswordVisible] = useState(false);
  const [phoneNumber, setPhoneNumber] = useState("");

  const isLogin = mode === "login";

  return (
    <div className="sidebar-auth-form">
      <div className="sidebar-auth-header">
        <h3>{isLogin ? "Log In" : "Sign Up"}</h3>
        <button className="sidebar-auth-close" onClick={onClose}>
          <i className="fa-solid fa-xmark"></i>
        </button>
      </div>

      <form onSubmit={(e) => e.preventDefault()}>
        {!isLogin && (
          <input type="text" placeholder="Full Name" className="sidebar-auth-input" required />
        )}
        <input type="email" placeholder="Email" className="sidebar-auth-input" required />
        <div className="sidebar-auth-password">
          <input
            type={passwordVisible ? "text" : "password"}
            placeholder="Password"
            className="sidebar-auth-input"
            required
          />
          <span
            className="sidebar-auth-toggle-pw"
            onClick={() => setPasswordVisible(!passwordVisible)}
          >
            {passwordVisible ? "👁️" : "👁️‍🗨️"}
          </span>
        </div>
        <button type="submit" className="sidebar-auth-submit">
          {isLogin ? "Log In" : "Sign Up"}
        </button>
      </form>

      <div className="sidebar-auth-switch">
        {isLogin ? "Don't have an account? " : "Already have an account? "}
        <span onClick={() => onSwitchMode(isLogin ? "signup" : "login")}>
          {isLogin ? "Sign Up" : "Log In"}
        </span>
      </div>

      <div className="sidebar-auth-divider">
        <span>OR</span>
      </div>

      <div className="sidebar-auth-social">
        <input
          type="tel"
          placeholder="Enter Phone Number"
          value={phoneNumber}
          onChange={(e) => setPhoneNumber(e.target.value)}
          className="sidebar-auth-input"
        />
        <button
          className="sidebar-auth-social-btn"
          onClick={() => loginWithRedirect({
            connection: "sms",
            login_hint: phoneNumber,
            appState: { returnTo: "/home" }
          })}
        >
          <img src="phone.png" alt="Phone" /> Phone
        </button>
        <button
          className="sidebar-auth-social-btn"
          onClick={() => loginWithRedirect({
            connection: "google-oauth2",
            appState: { returnTo: "/home" }
          })}
        >
          <img src="google-icon.png" alt="Google" /> Google
        </button>
        <button
          className="sidebar-auth-social-btn"
          onClick={() => loginWithRedirect({
            connection: "windowslive",
            appState: { returnTo: "/home" }
          })}
        >
          <img src="microsoft-icon.png" alt="Microsoft" /> Microsoft
        </button>
        <button className="sidebar-auth-social-btn" disabled>
          <img src="apple-icon.png" alt="Apple" /> Apple
        </button>
      </div>
    </div>
  );
}

const getCategoryIcon = (category) => {
  if (!category) return "fa-solid fa-file-medical";

  const c = category.toLowerCase();

  if (c.includes("cardio")) return "fa-solid fa-heart";
  if (c.includes("neuro")) return "fa-solid fa-brain";
  if (c.includes("derma")) return "fa-solid fa-hand-dots";
  if (c.includes("gastro")) return "fa-solid fa-stomach";
  if (c.includes("renal")) return "fa-solid fa-filter";
  if (c.includes("pulmo")) return "fa-solid fa-lungs";
  if (c.includes("psych")) return "fa-solid fa-brain";
  if (c.includes("pediatric")) return "fa-solid fa-baby";

  return "fa-solid fa-file-medical";
};
const getCategoryColor = (category = "") => {
  const lower = category.toLowerCase();

  if (lower.includes("cardio") || lower.includes("heart")) {
    return "#e11d48"; // ❤️ red
  }
  if (lower.includes("neuro") || lower.includes("brain")) {
    return "#7c3aed"; // 🧠 purple
  }
  if (lower.includes("pulmo") || lower.includes("lung")) {
    return "#0ea5e9"; // 🫁 sky blue
  }
  if (lower.includes("gastro") || lower.includes("liver")) {
    return "#f59e0b"; // 🟡 amber
  }
  if (lower.includes("renal") || lower.includes("kidney")) {
    return "#06b6d4"; // 🔵 cyan
  }
  if (lower.includes("infect") || lower.includes("virus")) {
    return "#ef4444"; // 🔴 bright red
  }

  return "#64748b"; // default gray
};
function Sidebar({
  qaHistory,
  searchHistory,
  clinicalFrame,
  threadContext,
  useGemini,
  onUseGeminiChange,
  onHistoryItemClick,
  onNewChatThread,
  onThreadSelect,
  onSidebarToggle,
}) {
  const navigate = useNavigate();
  const { user, isAuthenticated, logout } = useAuth0();
  const [searchQuery, setSearchQuery] = useState("");

  // ⭐ AUTH VIEW STATE: null = normal sidebar, "login" | "signup" = show auth form
  const [authView, setAuthView] = useState(null);

  // ⭐ ORIGINAL SIDEBAR STATES
  const [isSidebarVisible, setIsSidebarVisible] = useState(false);
  const [isPinned, setIsPinned] = useState(false);
  const threadSearchRef = useRef(null);
  useEffect(() => {
    window.openSidebar = () => setIsSidebarVisible(true);
    window.openSidebarSearch = () => {
      setIsSidebarVisible(true);
      setTimeout(() => threadSearchRef.current?.focus(), 150);
    };
  }, []);


  const [threads, setThreads] = useState([]);
  const [activeThreadId, setActiveThreadId] = useState(null);
  const [showProfileMenu, setShowProfileMenu] = useState(false);
  const [openMenuId, setOpenMenuId] = useState(null);
  const [expandedThreadId, setExpandedThreadId] = useState(null);
  const toggleMenu = (id) => {
  setOpenMenuId(prev => (prev === id ? null : id));
};

const handleRename = (id) => {
  const newName = prompt("Enter new thread name");
  if (!newName) return;

  const updated = threads.map(t =>
    t.id === id ? { ...t, name: newName } : t
  );

  setThreads(updated);
  sessionStorage.setItem("chatThreads", JSON.stringify(updated));
  setOpenMenuId(null);
};

const handleDelete = (id) => {
  if (!window.confirm("Delete this thread?")) return;

  const element = document.getElementById(`thread-${id}`);
  if (!element) return;

  element.classList.add("bubble-explode");

  // Create many bubbles
  for (let i = 0; i < 20; i++) {
    const bubble = document.createElement("span");
    bubble.className = "bubble-particle";

    const size = Math.random() * 12 + 6;
    bubble.style.width = `${size}px`;
    bubble.style.height = `${size}px`;

    bubble.style.left = `${Math.random() * 100}%`;
    bubble.style.top = `${Math.random() * 100}%`;

    bubble.style.animationDelay = `${Math.random() * 0.2}s`;

    element.appendChild(bubble);
  }

  setTimeout(() => {
    const updated = threads.filter((t) => t.id !== id);
    setThreads(updated);
    sessionStorage.setItem("chatThreads", JSON.stringify(updated));
    setOpenMenuId(null);

    if (id === activeThreadId && updated.length > 0) {
      handleSelectThread(updated[0].id);
    }
  }, 700);
};
  // ⭐ Hamburger trigger hook
  useEffect(() => {
    if (window.openSidebar) {
      window.openSidebar.current = () => setIsSidebarVisible(true);
    }
  }, []);

  /* AUTO CLOSE PROFILE MENU & AUTH VIEW when sidebar hides */
  useEffect(() => {
    if (!isSidebarVisible && !isPinned) {
      setShowProfileMenu(false);
      setAuthView(null);
    }
  }, [isSidebarVisible, isPinned]);
  useEffect(() => {
  const handleClickOutside = (e) => {
    const menu = document.querySelector(".thread-menu-wrapper");
    if (menu && !menu.contains(e.target)) {
      setOpenMenuId(null);
    }
  };

  document.addEventListener("mousedown", handleClickOutside);
  return () => document.removeEventListener("mousedown", handleClickOutside);
}, []);


/* LOAD THREADS */
useEffect(() => {
  const saved = JSON.parse(sessionStorage.getItem("chatThreads")) || [];

  const normalized = saved.map((t) => ({
    ...t,
    createdAt: t.createdAt || new Date().toISOString(),
    subtitle: t.subtitle || "Start a new medical assessment...",
    status: t.status || "PROCESSING",
    insights: t.insights || 0,
  }));

  if (normalized.length === 0) {
    normalized.push({
      id: Date.now(),
      name: "New Clinical Query",
      subtitle: "Start a new medical assessment...",
      status: "PROCESSING",
      insights: 0,
      createdAt: new Date().toISOString(),
      qaHistory: [],
      searches: [],
    });
  }

  setThreads(normalized);
  setActiveThreadId(normalized[0].id);
  sessionStorage.setItem("chatThreads", JSON.stringify(normalized));
}, []);


  /* SYNC ACTIVE THREAD — read-modify-write sessionStorage to preserve background stream data */
  useEffect(() => {
  if (!activeThreadId) return;

  // Read sessionStorage first so background stream updates for other threads are preserved
  const stored = JSON.parse(sessionStorage.getItem("chatThreads")) || [];
  const storedMap = {};
  stored.forEach(t => { storedMap[t.id] = t; });

  setThreads((prev) => {
    const updated = prev.map((t) => {
      if (t.id == activeThreadId) {
        // Update active thread with current React state
        return {
          ...t,
          qaHistory,
          searches: searchHistory,
          name: clinicalFrame || t.name,
          clinicalFrame: clinicalFrame || t.clinicalFrame,
          threadContext: threadContext || t.threadContext,
          subtitle: searchHistory?.[searchHistory.length - 1] || t.subtitle,
          insights: qaHistory?.length || 0,
          status: qaHistory?.length > 0 ? "VERIFIED" : "PROCESSING",
        };
      }
      // For non-active threads, merge sessionStorage data (preserves background stream updates)
      return storedMap[t.id] || t;
    });

    sessionStorage.setItem("chatThreads", JSON.stringify(updated));
    return updated;
  });
}, [qaHistory, searchHistory, clinicalFrame, threadContext, activeThreadId]);


  /* HANDLERS */
  const handleSelectThread = (id) => {
    setActiveThreadId(id);
    sessionStorage.setItem("currentThreadId", id);

    // Read from sessionStorage for latest data (includes background stream updates)
    const stored = JSON.parse(sessionStorage.getItem("chatThreads")) || [];
    const selected = stored.find((t) => t.id == id) || threads.find((t) => t.id === id);
    if (selected && onThreadSelect) {
      onThreadSelect(selected.qaHistory, selected.searches, selected.threadContext);
    }
  };

  const handleCreateThread = () => {
    const newThread = {
      id: Date.now(),
      name: `New Clinical Query`,
      subtitle: "Start a new medical assessment...",
      status: "PROCESSING",
      insights: 0,
      createdAt: new Date().toISOString(),
      qaHistory: [],
      searches: [],
    };


    const updated = [newThread, ...threads];
    setThreads(updated);
    setActiveThreadId(newThread.id);
    sessionStorage.setItem("currentThreadId", newThread.id);
    sessionStorage.setItem("chatThreads", JSON.stringify(updated));

    onNewChatThread && onNewChatThread();
  };

  const handlePinToggle = () => {
    const next = !isPinned;
    setIsPinned(next);
    setIsSidebarVisible(next);
    onSidebarToggle && onSidebarToggle(next ? "sidebar-open" : "");
  };

  const goHome = () => {
    sessionStorage.removeItem("currentThreadId");
    onNewChatThread && onNewChatThread();
    navigate("/", { replace: true });
  };

  const filteredThreads = threads.filter((t) =>
  t.name.toLowerCase().includes(searchQuery.toLowerCase())
);

const groupThreads = () => {
  const today = [];
  const yesterday = [];
  const last7 = [];
  const now = new Date();

  filteredThreads.forEach((thread) => {
    const created = new Date(thread.createdAt || new Date());
    const diff = (now - created) / (1000 * 60 * 60 * 24);

    if (diff < 1) today.push(thread);
    else if (diff < 2) yesterday.push(thread);
    else last7.push(thread);
  });

  return { today, yesterday, last7 };
};

const { today, yesterday, last7 } = groupThreads();

const renderThreadCard = (thread) => {
  const isActive = activeThreadId === thread.id;
  const isExpanded = expandedThreadId === thread.id;
  const questionCount = thread.qaHistory?.length || 0;

  return (
    <div
      id={`thread-${thread.id}`}
      key={thread.id}
      className={`thread-card ${isActive ? "active" : ""} ${isExpanded ? "expanded" : ""}`}
      onClick={() => handleSelectThread(thread.id)}
    >
      <div className="thread-card-header">
        <div className="thread-left-section">
          <div className="thread-title-row">
            <div
              className="thread-icon-circle"
              style={{ background: getCategoryColor(thread.category) }}
            >
              <i className={getCategoryIcon(thread.category)}></i>
            </div>
            <span className="thread-title">{thread.name || "New Clinical Query"}</span>
          </div>
        </div>
        <div className="thread-actions">
          {questionCount > 0 && (
            <span className="thread-question-count" title={`${questionCount} question${questionCount !== 1 ? 's' : ''}`}>
              <i className="fa-solid fa-comments"></i> {questionCount}
            </span>
          )}
          {questionCount > 0 && (
            <button
              className="icon-btn thread-expand-btn"
              onClick={(e) => {
                e.stopPropagation();
                setExpandedThreadId(isExpanded ? null : thread.id);
              }}
              title={isExpanded ? "Collapse questions" : "Show questions"}
            >
              <i className={`fa-solid fa-chevron-${isExpanded ? "up" : "down"}`}></i>
            </button>
          )}
          <div className="thread-menu-wrapper">
            <button
              className="icon-btn menu-dots"
              onClick={(e) => {
                e.stopPropagation();
                toggleMenu(thread.id);
              }}
            >
              <i className="fa-solid fa-ellipsis-vertical"></i>
            </button>
            {openMenuId === thread.id && (
              <div className="thread-menu">
                <div onClick={() => handleRename(thread.id)}>Rename</div>
                <div onClick={() => handleDelete(thread.id)}>Delete</div>
              </div>
            )}
          </div>
        </div>
      </div>

      <div className="thread-card-subtitle">
        {thread.subtitle?.length > 120
          ? thread.subtitle.substring(0, 120) + "..."
          : thread.subtitle}
      </div>

      {/* Expandable question timeline */}
      {isExpanded && questionCount > 0 && (
        <div className="thread-questions-list">
          {thread.qaHistory.map((qa, i) => (
            <div
              key={i}
              className="thread-question-item"
              onClick={(e) => {
                e.stopPropagation();
                handleSelectThread(thread.id);
              }}
              title={qa.question}
            >
              <span className="thread-question-number">{i + 1}</span>
              <span className="thread-question-text">
                {qa.question?.length > 80 ? qa.question.substring(0, 80) + "..." : qa.question}
              </span>
              {qa.answer === "loading" && (
                <i className="fa-solid fa-spinner fa-spin thread-question-loading"></i>
              )}
            </div>
          ))}
        </div>
      )}

      <div className="thread-card-footer">
        <div className="footer-left">
          <span className={`status-badge ${thread.status.toLowerCase()}`}>
            {thread.status}
          </span>
          <span className="insight-badge">
            {thread.insights} Insights
          </span>
        </div>
        <div className="footer-icons">
          <button className="icon-btn footer-icon">
            <i className="fa-solid fa-cloud-arrow-up"></i>
          </button>
          <button className="icon-btn footer-icon">
            <i className="fa-solid fa-clipboard-list"></i>
          </button>
          <button className="icon-btn small-tick">
            <i className="fa-regular fa-circle-check"></i>
          </button>
        </div>
      </div>
    </div>
  );
};


  return (
    <aside
      className={`sidebar ${
        isPinned ? "pinned" : isSidebarVisible ? "open" : ""
      }`}
      onMouseEnter={() => setIsSidebarVisible(true)}
      onMouseLeave={() => !isPinned && !authView && setIsSidebarVisible(false)}
    >
      {/* HEADER */}
     <div className="sidebar-header">
  <div className="brand-wrapper" onClick={goHome}>
    <img src={dmiLogo} alt="DeepMedIQ Logo" className="brand-logo-img" />
    <span className="brand-name">DeepMedIQ</span>
  </div>

  <button className="pin-button" onClick={handlePinToggle}>
    📌
  </button>
</div>



<div className="top-actions">
  <button className="top-btn" onClick={goHome}>
    <i className="fa-solid fa-house"></i> Home
  </button>

  <button className="top-btn" onClick={() => navigate("/landing")}>
    <i className="fa-solid fa-compass"></i> Discover
  </button>

</div>

<div className="sidebar-divider"></div>
  {!authView && (
    <>
      <div className="threads-section">
        <div className="threads-header">
          <div className="threads-title-left">
            <span>THREADS</span>
            <span className="threads-count">({threads.length})</span>
          </div>

          <button
            className="new-thread-inline"
            onClick={handleCreateThread}
          >
            + New Thread
          </button>
        </div>

        <input
          ref={threadSearchRef}
          type="text"
          placeholder="Search threads..."
          className="thread-search"
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
        />
      </div>

      <div className="threads-list">
        {today.length > 0 && (
          <>
            <div className="threads-group-title">TODAY</div>
            {today.map(renderThreadCard)}
          </>
        )}

        {yesterday.length > 0 && (
          <>
            <div className="threads-group-title">YESTERDAY</div>
            {yesterday.map(renderThreadCard)}
          </>
        )}

        {last7.length > 0 && (
          <>
            <div className="threads-group-title">LAST 7 DAYS</div>
            {last7.map(renderThreadCard)}
          </>
        )}
      </div>
    </>
  )}

      {/* MODEL TOGGLE — hidden */}

      {/* ─── INLINE AUTH FORM (replaces threads when active) ─── */}
      {authView && (isPinned || isSidebarVisible) && (
        <SidebarAuthForm
          key={authView}
          mode={authView}
          onSwitchMode={setAuthView}
          onClose={() => setAuthView(null)}
        />
      )}

      {/* PROFILE FOOTER */}
      {(isPinned || isSidebarVisible) && (
        <div className="sidebar-footer">
          {isAuthenticated && user ? (
            <>
              <div
                className="profile-row"
                onClick={() => setShowProfileMenu((prev) => !prev)}
              >
                <div className="avatar">
                  {user.name?.charAt(0).toUpperCase()}
                </div>

                {showProfileMenu && (
                  <div>
                    <div className="profile-name">{user.name}</div>
                    <div className="profile-email">{user.email}</div>
                  </div>
                )}
              </div>

              {showProfileMenu && (
                <div className="footer-actions">
                  <button
                    className="sidebar-footer-auth-link"
                    onClick={() => setAuthView(authView ? null : "login")}
                  >
                    Switch Account
                  </button>
                  <button
                    className="primary"
                    onClick={() =>
                      logout({ returnTo: window.location.origin })
                    }
                  >
                    Logout
                  </button>
                </div>
              )}
            </>
          ) : (
            <div className="sidebar-auth-buttons">
              <button
                className="sidebar-login-btn"
                onClick={() => setAuthView("login")}
              >
                Log In
              </button>
              <button
                className="sidebar-signup-btn"
                onClick={() => setAuthView("signup")}
              >
                Sign Up
              </button>
            </div>
          )}
        </div>
      )}
    </aside>
  );
}

export default Sidebar;